import { Badge, Button, Modal, Table } from 'react-bootstrap';

import type { LinhaPreviewImportAutorNatureza } from '@/modules/reports/presentation/utils/autor-natureza-excel-import.utils';
import type { LinhaPreviewImportNumeroProcesso } from '@/modules/reports/presentation/utils/numero-processo-excel-import.utils';
import type {
  LinhaPreviewAudienciaPresencial,
  LinhaPreviewAudienciaRemota,
  LinhaPreviewDesignacaoAudiencia,
  LinhaPreviewDesignacaoProcesso,
} from '@/modules/reports/presentation/utils/relatorio-civel-etapas-8-14-15-excel-import.utils';

export type ModalImportarExcelRelatorioCivelProps =
  | {
      variant: 'autor-natureza';
      show: boolean;
      linhas: LinhaPreviewImportAutorNatureza[];
      onHide: () => void;
      onConfirmar: () => void;
    }
  | {
      variant: 'numero-processo';
      show: boolean;
      tituloSecao: string;
      linhas: LinhaPreviewImportNumeroProcesso[];
      onHide: () => void;
      onConfirmar: () => void;
    }
  | {
      variant: 'audiencias-presencial';
      show: boolean;
      linhas: LinhaPreviewAudienciaPresencial[];
      onHide: () => void;
      onConfirmar: () => void;
    }
  | {
      variant: 'audiencias-remota';
      show: boolean;
      linhas: LinhaPreviewAudienciaRemota[];
      onHide: () => void;
      onConfirmar: () => void;
    }
  | {
      variant: 'designacao-processo';
      show: boolean;
      linhas: LinhaPreviewDesignacaoProcesso[];
      onHide: () => void;
      onConfirmar: () => void;
    }
  | {
      variant: 'designacao-audiencia';
      show: boolean;
      linhas: LinhaPreviewDesignacaoAudiencia[];
      onHide: () => void;
      onConfirmar: () => void;
    };

function ResumoValidacao({ validas, invalidas }: { validas: number; invalidas: number }) {
  return (
    <div className="d-flex flex-wrap gap-2 mb-3">
      <Badge bg="success">Válidas: {validas}</Badge>
      <Badge bg="danger">Com problema: {invalidas}</Badge>
    </div>
  );
}

export default function ModalImportarExcelRelatorioCivel(props: ModalImportarExcelRelatorioCivelProps) {
  if (props.variant === 'autor-natureza') {
    const { show, linhas, onHide, onConfirmar } = props;
    const validas = linhas.filter((l) => l.valido);
    const invalidas = linhas.filter((l) => !l.valido);

    return (
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Importação — Autor e Natureza da ação
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {invalidas.length > 0 ? (
              <>
                Algumas linhas não passaram na validação (autor ou natureza em branco, ou natureza não reconhecida).
                Ao confirmar, <strong>apenas as linhas válidas</strong> serão adicionadas à lista.
              </>
            ) : (
              <>
                Revise o conteúdo abaixo. Ao confirmar, as linhas serão <strong>adicionadas à lista</strong> de autor e
                natureza da ação.
              </>
            )}
          </p>
          <ResumoValidacao validas={validas.length} invalidas={invalidas.length} />
          <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Linha</th>
                  <th>Autor</th>
                  <th>Natureza (planilha)</th>
                  <th>Natureza (sistema)</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={`${l.indicePlanilha}-${idx}`}>
                    <td>{l.indicePlanilha}</td>
                    <td>{l.autor || <span className="text-muted">—</span>}</td>
                    <td>{l.naturezaTexto || <span className="text-muted">—</span>}</td>
                    <td>{l.naturezaLabel || <span className="text-muted">—</span>}</td>
                    <td>
                      {l.valido ? (
                        <Badge bg="success">Válida</Badge>
                      ) : (
                        <>
                          <Badge bg="danger" className="me-1">
                            Inválida
                          </Badge>
                          <small className="text-danger d-block mt-1">{l.erros.join(' ')}</small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar importação
          </Button>
          <Button variant="primary" onClick={onConfirmar} disabled={validas.length === 0}>
            Confirmar importação ({validas.length} linha{validas.length !== 1 ? 's' : ''})
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (props.variant === 'numero-processo') {
    const { show, tituloSecao, linhas, onHide, onConfirmar } = props;
    const validas = linhas.filter((l) => l.valido);
    const invalidas = linhas.filter((l) => !l.valido);

    return (
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Importação — {tituloSecao}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Revise os números abaixo. Ao confirmar, serão <strong>adicionados à lista</strong> como texto (sem alterar
            a formatação importada). Números repetidos são permitidos; linhas em branco na planilha são ignoradas.
          </p>
          <ResumoValidacao validas={validas.length} invalidas={invalidas.length} />
          <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Linha</th>
                  <th>Número do processo</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={`${l.indicePlanilha}-${idx}`}>
                    <td>{l.indicePlanilha}</td>
                    <td>
                      {l.numeroProcesso ? (
                        <span className="font-monospace">{l.numeroProcesso}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {l.valido ? (
                        <Badge bg="success">Válida</Badge>
                      ) : (
                        <>
                          <Badge bg="danger" className="me-1">
                            Inválida
                          </Badge>
                          <small className="text-danger d-block mt-1">{l.erros.join(' ')}</small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar importação
          </Button>
          <Button variant="primary" onClick={onConfirmar} disabled={validas.length === 0}>
            Confirmar importação ({validas.length} linha{validas.length !== 1 ? 's' : ''})
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (props.variant === 'audiencias-presencial') {
    const { show, linhas, onHide, onConfirmar } = props;
    const validas = linhas.filter((l) => l.valido);
    const invalidas = linhas.filter((l) => !l.valido);
    return (
      <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Importação — Audiências presenciais
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {invalidas.length > 0 ? (
              <>
                Algumas linhas têm erro (tipo não reconhecido, protocolo em branco ou data inválida). Ao confirmar,{' '}
                <strong>apenas as válidas</strong> serão adicionadas.
              </>
            ) : (
              <>Revise os dados. Ao confirmar, as linhas serão adicionadas à lista de audiências presenciais.</>
            )}
          </p>
          <ResumoValidacao validas={validas.length} invalidas={invalidas.length} />
          <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Linha</th>
                  <th>Tipo (planilha)</th>
                  <th>Tipo (sistema)</th>
                  <th>N.º protocolo</th>
                  <th>Data</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={`${l.indicePlanilha}-${idx}`}>
                    <td>{l.indicePlanilha}</td>
                    <td>{l.tipoTexto || <span className="text-muted">—</span>}</td>
                    <td>{l.tipoResolvido || <span className="text-muted">—</span>}</td>
                    <td className="font-monospace">{l.numeroProtocolo || <span className="text-muted">—</span>}</td>
                    <td>{l.dataTexto || <span className="text-muted">—</span>}</td>
                    <td>
                      {l.valido ? (
                        <Badge bg="success">Válida</Badge>
                      ) : (
                        <>
                          <Badge bg="danger" className="me-1">
                            Inválida
                          </Badge>
                          <small className="text-danger d-block mt-1">{l.erros.join(' ')}</small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar importação
          </Button>
          <Button variant="primary" onClick={onConfirmar} disabled={validas.length === 0}>
            Confirmar importação ({validas.length} linha{validas.length !== 1 ? 's' : ''})
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (props.variant === 'audiencias-remota') {
    const { show, linhas, onHide, onConfirmar } = props;
    const validas = linhas.filter((l) => l.valido);
    const invalidas = linhas.filter((l) => !l.valido);
    return (
      <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Importação — Audiências remotas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {invalidas.length > 0 ? (
              <>
                Algumas linhas têm erro (tipo não reconhecido, processo em branco ou data inválida). Ao confirmar,{' '}
                <strong>apenas as válidas</strong> serão adicionadas.
              </>
            ) : (
              <>Revise os dados. Ao confirmar, as linhas serão adicionadas à lista de audiências remotas.</>
            )}
          </p>
          <ResumoValidacao validas={validas.length} invalidas={invalidas.length} />
          <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Linha</th>
                  <th>Tipo (planilha)</th>
                  <th>Tipo (sistema)</th>
                  <th>N.º processo</th>
                  <th>Data</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={`${l.indicePlanilha}-${idx}`}>
                    <td>{l.indicePlanilha}</td>
                    <td>{l.tipoTexto || <span className="text-muted">—</span>}</td>
                    <td>{l.tipoResolvido || <span className="text-muted">—</span>}</td>
                    <td className="font-monospace">{l.numeroProcesso || <span className="text-muted">—</span>}</td>
                    <td>{l.dataTexto || <span className="text-muted">—</span>}</td>
                    <td>
                      {l.valido ? (
                        <Badge bg="success">Válida</Badge>
                      ) : (
                        <>
                          <Badge bg="danger" className="me-1">
                            Inválida
                          </Badge>
                          <small className="text-danger d-block mt-1">{l.erros.join(' ')}</small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar importação
          </Button>
          <Button variant="primary" onClick={onConfirmar} disabled={validas.length === 0}>
            Confirmar importação ({validas.length} linha{validas.length !== 1 ? 's' : ''})
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (props.variant === 'designacao-processo') {
    const { show, linhas, onHide, onConfirmar } = props;
    const validas = linhas.filter((l) => l.valido);
    const invalidas = linhas.filter((l) => !l.valido);
    return (
      <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-earmark-spreadsheet me-2"></i>
            Importação — Designação especial (processos)
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {invalidas.length > 0 ? (
              <>
                Corrija na planilha ou confirme para importar <strong>somente as linhas válidas</strong> (defensoria
                deve coincidir com a lista do sistema).
              </>
            ) : (
              <>Revise tipo de peça, processo e defensoria. Ao confirmar, os itens serão adicionados à lista.</>
            )}
          </p>
          <ResumoValidacao validas={validas.length} invalidas={invalidas.length} />
          <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>Linha</th>
                  <th>Tipo de peça</th>
                  <th>N.º processo</th>
                  <th>Defensoria (planilha)</th>
                  <th>Defensoria (sistema)</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l, idx) => (
                  <tr key={`${l.indicePlanilha}-${idx}`}>
                    <td>{l.indicePlanilha}</td>
                    <td>{l.tipoPeca || <span className="text-muted">—</span>}</td>
                    <td className="font-monospace">{l.numeroProcesso || <span className="text-muted">—</span>}</td>
                    <td>{l.defensoriaTexto || <span className="text-muted">—</span>}</td>
                    <td>{l.defensoriaLabel || <span className="text-muted">—</span>}</td>
                    <td>
                      {l.valido ? (
                        <Badge bg="success">Válida</Badge>
                      ) : (
                        <>
                          <Badge bg="danger" className="me-1">
                            Inválida
                          </Badge>
                          <small className="text-danger d-block mt-1">{l.erros.join(' ')}</small>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar importação
          </Button>
          <Button variant="primary" onClick={onConfirmar} disabled={validas.length === 0}>
            Confirmar importação ({validas.length} linha{validas.length !== 1 ? 's' : ''})
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const { show, linhas, onHide, onConfirmar } = props;
  const validas = linhas.filter((l) => l.valido);
  const invalidas = linhas.filter((l) => !l.valido);
  return (
    <Modal show={show} onHide={onHide} centered size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-file-earmark-spreadsheet me-2"></i>
          Importação — Designação especial (audiências)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          {invalidas.length > 0 ? (
            <>
              Corrija na planilha ou confirme para importar <strong>somente as linhas válidas</strong> (defensoria e
              data obrigatórias).
            </>
          ) : (
            <>Revise audiência, processo, data e defensoria. Ao confirmar, os itens serão adicionados à lista.</>
          )}
        </p>
        <ResumoValidacao validas={validas.length} invalidas={invalidas.length} />
        <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
          <Table striped bordered hover size="sm" className="mb-0">
            <thead className="table-light sticky-top">
              <tr>
                <th>Linha</th>
                <th>Audiência / tipo</th>
                <th>N.º processo</th>
                <th>Data</th>
                <th>Defensoria (planilha)</th>
                <th>Defensoria (sistema)</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, idx) => (
                <tr key={`${l.indicePlanilha}-${idx}`}>
                  <td>{l.indicePlanilha}</td>
                  <td>{l.audienciaTexto || <span className="text-muted">—</span>}</td>
                  <td className="font-monospace">{l.numeroProcesso || <span className="text-muted">—</span>}</td>
                  <td>{l.dataTexto || <span className="text-muted">—</span>}</td>
                  <td>{l.defensoriaTexto || <span className="text-muted">—</span>}</td>
                  <td>{l.defensoriaLabel || <span className="text-muted">—</span>}</td>
                  <td>
                    {l.valido ? (
                      <Badge bg="success">Válida</Badge>
                    ) : (
                      <>
                        <Badge bg="danger" className="me-1">
                          Inválida
                        </Badge>
                        <small className="text-danger d-block mt-1">{l.erros.join(' ')}</small>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar importação
        </Button>
        <Button variant="primary" onClick={onConfirmar} disabled={validas.length === 0}>
          Confirmar importação ({validas.length} linha{validas.length !== 1 ? 's' : ''})
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
